import * as React from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  type BannerRatio,
  DEFAULT_BRAND,
  ListingBanner,
  bannerHeight,
} from '@proptr/creative-templates';
import {
  LOOKBOOK_DEFAULT_BRAND,
  ListingLookbook,
} from '@proptr/creative-templates';

// Headless render target. Puppeteer navigates here and screenshots
// #creative, so exported creatives run the SAME components as the in-app
// preview — there is no second implementation to keep in sync.
//
// Unauthenticated by design: it renders only what is in the URL and
// touches no account data. Reachable only from the render service.

declare global {
  interface Window {
    __creativeReady?: boolean;
  }
}

export function RenderPage() {
  const { template } = useParams<{ template: string }>();
  const [params] = useSearchParams();

  const payload = React.useMemo(() => {
    const d = params.get('d');
    if (!d) return null;
    try {
      // base64url → JSON. Avoids URL-length and escaping problems that
      // a query-string-per-field encoding would hit.
      const json = atob(d.replace(/-/g, '+').replace(/_/g, '/'));
      return JSON.parse(json) as {
        brand: any;
        listing: any;
        fonts?: { family: string; url: string }[];
      };
    } catch {
      return null;
    }
  }, [params]);

  // `?r=9x16` selects the vertical canvas. Colons aren't safe in a query
  // string, so the wire form uses `x`. Anything unrecognised falls back to
  // square rather than erroring — a bad param must not fail an export.
  //
  // Derived as a plain string, and the ready-gate below depends on THAT
  // rather than on `params`: useSearchParams hands back a fresh object on
  // every render, so depending on it re-runs the effect each time the
  // template's auto-fit sets state, and __creativeReady never stays true
  // long enough for Puppeteer to see it.
  const ratio: BannerRatio = params.get('r') === '9x16' ? '9:16' : '1:1';

  // Signal the screenshotter only once the fonts have landed AND the
  // templates' auto-fit has had a frame to settle. Screenshotting earlier
  // catches a price measured against the fallback font.
  React.useEffect(() => {
    let cancelled = false;
    window.__creativeReady = false;

    // Uploaded typefaces aren't in any stylesheet — register them with
    // the FontFace API and await them BEFORE signalling ready, or the
    // templates' auto-fit measures against the fallback and bakes in the
    // wrong price size.
    const loadCustom = async () => {
      const fonts = payload?.fonts ?? [];
      await Promise.all(
        fonts.map(async (f) => {
          try {
            const face = new FontFace(f.family, `url(${f.url})`);
            await face.load();
            document.fonts.add(face);
          } catch {
            // A missing face falls back rather than blocking the render.
          }
        }),
      );
    };

    const settle = () =>
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          if (!cancelled) window.__creativeReady = true;
        }),
      );
    void loadCustom()
      .then(() => document.fonts?.ready)
      .then(settle);
    return () => {
      cancelled = true;
    };
  }, [payload, template, ratio]);

  if (!payload) {
    return <div style={{ font: '14px system-ui' }}>Missing render payload.</div>;
  }

  const isLookbook = template === 'listing-lookbook';

  // The lookbook has no vertical variant yet, so it always renders square.
  const height = isLookbook ? 1080 : bannerHeight(ratio);

  return (
    // Exactly the canvas, no chrome — the screenshot is of this element.
    <div
      id="creative"
      style={{ width: 1080, height, margin: 0, overflow: 'hidden' }}
    >
      {isLookbook ? (
        <ListingLookbook
          width={1080}
          brand={{ ...LOOKBOOK_DEFAULT_BRAND, ...payload.brand, radius: 0 }}
          listing={payload.listing}
        />
      ) : (
        <ListingBanner
          width={1080}
          ratio={ratio}
          brand={{ ...DEFAULT_BRAND, ...payload.brand, radius: 0 }}
          listing={payload.listing}
        />
      )}
    </div>
  );
}
